import React, { useEffect, useRef } from 'react';

/**
 * MedicalAnimation - Network Particle System
 * Recreated from CommunityCare_Startpage.mp4
 * Features: Connected cyan nodes flowing diagonally on dark background
 */
const MedicalAnimation = () => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let width = window.innerWidth;
        let height = window.innerHeight;

        const resizeCanvas = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width;
            canvas.height = height;
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // Animation state
        let time = 0;

        // Configuration matching video style
        const config = {
            particleCount: Math.floor((width * height) / 9000), // Density
            connectionDistance: 150,
            baseSpeed: 0.5,
            colors: {
                bgTop: '#020617',    // Deep dark blue/black
                bgBottom: '#0f172a', // Slightly lighter slate blue
                particle: '#00e5ff', // Cyan
                line: 'rgba(0, 229, 255, 0.2)',
            }
        };

        // Particle System
        class Particle {
            constructor() {
                this.reset(true);
            }

            reset(randomScreenPos = false) {
                if (randomScreenPos) {
                    this.x = Math.random() * width;
                    this.y = Math.random() * height;
                } else {
                    // Spawn from bottom-right area mostly
                    if (Math.random() > 0.5) {
                        this.x = width + 10;
                        this.y = Math.random() * height;
                    } else {
                        this.x = Math.random() * width;
                        this.y = height + 10;
                    }
                }

                // Slight size variation
                this.size = Math.random() * 2 + 1.5;

                // Diagonal movement (Bottom-Right to Top-Left)
                const angle = Math.PI * 1.25; // Diagonally up-left
                const speed = Math.random() * config.baseSpeed + 0.2;

                this.vx = Math.cos(angle) * speed;
                this.vy = Math.sin(angle) * speed;

                // Opacity pulse
                this.opacity = Math.random() * 0.5 + 0.3;
                this.pulseSpeed = Math.random() * 0.02 + 0.01;
                this.pulseOffset = Math.random() * Math.PI * 2;
            }

            update() {
                this.x += this.vx;
                this.y += this.vy;

                // Reset if out of bounds (Top or Left)
                if (this.x < -20 || this.y < -20) {
                    this.reset(false);
                }
            }

            draw(ctx, time) {
                const pulse = Math.sin(time * this.pulseSpeed + this.pulseOffset) * 0.3 + 0.7;
                const currentOpacity = this.opacity * pulse;

                // Soft glow
                const gradient = ctx.createRadialGradient(
                    this.x, this.y, 0,
                    this.x, this.y, this.size * 5
                );
                gradient.addColorStop(0, `rgba(0, 229, 255, ${currentOpacity})`);
                gradient.addColorStop(0.5, `rgba(0, 229, 255, ${currentOpacity * 0.2})`);
                gradient.addColorStop(1, 'transparent');

                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size * 5, 0, Math.PI * 2);
                ctx.fillStyle = gradient;
                ctx.fill();

                // Core
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(220, 250, 255, ${currentOpacity + 0.2})`;
                ctx.fill();
            }
        }

        const particles = Array.from({ length: config.particleCount }, () => new Particle());

        // Main Loop
        const animate = () => {
            time++;

            // Background
            const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
            bgGradient.addColorStop(0, config.colors.bgTop);
            bgGradient.addColorStop(1, config.colors.bgBottom);
            ctx.fillStyle = bgGradient;
            ctx.fillRect(0, 0, width, height);

            // Update Particles
            particles.forEach(p => p.update());

            // Draw Connections
            ctx.beginPath();
            ctx.strokeStyle = config.colors.line;
            ctx.lineWidth = 0.8;

            for (let i = 0; i < particles.length; i++) {
                const p1 = particles[i];
                for (let j = i + 1; j < particles.length; j++) {
                    const p2 = particles[j];
                    const dx = p1.x - p2.x;
                    const dy = p1.y - p2.y;
                    const distSq = dx * dx + dy * dy;

                    if (distSq < config.connectionDistance * config.connectionDistance) {
                        const dist = Math.sqrt(distSq);
                        const alpha = 1 - dist / config.connectionDistance;

                        ctx.moveTo(p1.x, p1.y);
                        ctx.lineTo(p2.x, p2.y);

                        // Note: Setting strokeStyle inside loop is expensive, 
                        // but needed for distance-based opacity.
                        // Optimization: Batch lines by opacity buckets or use global average.
                        // For high fidelity to video, we try simple batching or single pass if acceptable.
                        // To keep it simple and performant, we'll just draw the line here with specific alpha?
                        // Actually, `ctx` state changes are costly. 
                        // Better approach: use a single stroke style and vary opacity? 
                        // Canvas doesn't support per-segment opacity in a single path easily.
                        // Individual paths is slower but gives correct look.
                    }
                }
            }
            // Optimized Drawing: Render lines individually for correct opacity (Video look)
            // Since particle count isn't massive ~150-200, this is acceptable for 2D context.
            for (let i = 0; i < particles.length; i++) {
                const p1 = particles[i];
                for (let j = i + 1; j < particles.length; j++) {
                    const p2 = particles[j];
                    const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);

                    if (dist < config.connectionDistance) {
                        const alpha = (1 - dist / config.connectionDistance) * 0.25;
                        ctx.beginPath();
                        ctx.strokeStyle = `rgba(0, 229, 255, ${alpha})`;
                        ctx.lineWidth = 1;
                        ctx.moveTo(p1.x, p1.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.stroke();
                    }
                }
            }

            // Draw Particles on top
            particles.forEach(p => p.draw(ctx, time));

            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 w-full h-full"
            style={{ zIndex: 0 }}
        />
    );
};

export default MedicalAnimation;
